import { formatDateTime } from '@if/core/utils'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { useIsDeprecated } from '@src/data/selectors/app-config-selectors'
import { useIsIcebergGrDevs } from '@src/providers/UserProvider'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { User } from '../../../data/types'
import { CreditApplicationReservation } from '../../../data/types/CreditApplicationReservation'

const PRESETS = [10, 20, 30]
// Heights of the fixed/sticky chrome above this banner so it pins right under it.
const APP_BAR_HEIGHT = 64
const ALERT_BANNER_HEIGHT = 48

interface Props {
  lock: CreditApplicationReservation | null
  currentUser: User | null
  handleLockCreditApplication: (minutes: number) => void
  handleAddTimeToLock: (lock: CreditApplicationReservation, minutes: number) => void
  handleDeleteLock: (lock: CreditApplicationReservation) => void
}

const CreditApplicationLockBanner = ({
  lock,
  currentUser,
  handleLockCreditApplication,
  handleAddTimeToLock,
  handleDeleteLock,
}: Props) => {
  const { t } = useTranslation()
  const isIcebergGrDevs = useIsIcebergGrDevs()
  const deprecated = useIsDeprecated()
  const hasAlertBanner = isIcebergGrDevs || (import.meta.env.PROD && deprecated)
  const stickyTop = hasAlertBanner ? APP_BAR_HEIGHT + ALERT_BANNER_HEIGHT : APP_BAR_HEIGHT

  const isActive = lock !== null && new Date(lock.expiresOn).getTime() > Date.now()
  const isMine = isActive && currentUser?.id === lock.userId

  // Reserved by another user => read-only warning, no actions (avoids agents fighting over a lock).
  const showActions = !isActive || isMine

  let severity: 'info' | 'success' | 'warning' = 'info'
  let message = t('lockCreditApplication.notReserved')
  if (isActive && isMine) {
    severity = 'success'
    message = t('lockCreditApplication.reservedByYouUntil', { time: formatDateTime(lock.expiresOn) })
  } else if (isActive) {
    severity = 'warning'
    message = t('lockCreditApplication.reservedByOtherUntil', {
      name: lock.userName,
      time: formatDateTime(lock.expiresOn),
    })
  }

  return (
    <Box sx={{ position: 'sticky', top: stickyTop, zIndex: 1100, mb: 2 }}>
      <Alert
        severity={severity}
        action={
          showActions && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {!isActive && (
              <>
                <Typography variant="body2">{t('lockCreditApplication.takingChargeFor')}</Typography>
                {PRESETS.map((minutes) => (
                  <Button
                    key={minutes}
                    size="small"
                    variant="outlined"
                    onClick={() => handleLockCreditApplication(minutes)}
                  >
                    {t('lockCreditApplication.minutesLabel', { count: minutes })}
                  </Button>
                ))}
              </>
            )}

            {isActive && isMine && (
              <>
                <Typography variant="body2">{t('lockCreditApplication.addTime')}</Typography>
                {PRESETS.map((minutes) => (
                  <Button
                    key={minutes}
                    size="small"
                    variant="outlined"
                    onClick={() => handleAddTimeToLock(lock, minutes)}
                  >
                    {t('lockCreditApplication.addMinutes', { count: minutes })}
                  </Button>
                ))}
              </>
            )}

            {isActive && isMine && (
              <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteLock(lock)}>
                {t('lockCreditApplication.release')}
              </Button>
            )}
          </Stack>
          )
        }
      >
        <Typography variant="body2" fontWeight="bold">
          {message}
        </Typography>
      </Alert>
    </Box>
  )
}

export default memo(CreditApplicationLockBanner)
